import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "./ContactUs.css";

/* ─── Small inline icons ─────────────────────────────────────────────── */
const IconMail = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
  </svg>
);
const IconClock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const IconMessageCircle = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);
const IconSend = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const IconCheckCircle = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

interface FormState { name: string; email: string; subject: string; message: string; }
const EMPTY_FORM: FormState = { name: "", email: "", subject: "", message: "" };

const ContactUs = () => {
  const [form, setForm]         = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors]     = useState<Partial<FormState>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<FormState> = {};
    if (!form.name.trim())    next.name    = "Please enter your name.";
    if (!form.email.trim())   next.email   = "Please enter your email.";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Enter a valid email address.";
    if (!form.subject.trim()) next.subject = "Please add a subject.";
    if (!form.message.trim()) next.message = "Please write a message.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    /*
     * TODO: wire this up to the real "send email" backend endpoint once it
     * exists, e.g.:
     *   await postRequest("/contact/send", form);
     * For now this only builds the UI, so we simulate a short delay and
     * show a success state locally — nothing is actually sent yet.
     */
    await new Promise(res => setTimeout(res, 900));
    setSubmitting(false);
    setSubmitted(true);
    setForm(EMPTY_FORM);
  };

  return (
    <>
      <Helmet>
        <title>Acadebility - Contact Us</title>
        <meta name="description" content="Get in touch with the Acadebility team — questions, feedback, or support requests." />
      </Helmet>

      <div className="lgl-page cu-page">
        {/* ── Hero ── */}
        <div className="lgl-hero">
          <div className="lgl-hero-inner">
            <nav className="lgl-breadcrumb">
              <Link to="/">Home</Link>
              <span className="lgl-sep">/</span>
              <span className="lgl-crumb-current">Contact Us</span>
            </nav>
            <h1 className="lgl-title">Get in touch</h1>
            <p className="lgl-subtitle">
              Questions, feedback, or something not working right? We'd like to hear about it.
            </p>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="lgl-body">
          <div className="lgl-body-inner cu-body-inner">
            <div className="cu-grid">

              {/* ── Info column ── */}
              <div className="cu-info">
                <div className="cu-info-card">
                  <div className="cu-info-icon"><IconMail /></div>
                  <div>
                    <h3>Email us</h3>
                    <a href="mailto:acadebility@gmail.com">acadebility@gmail.com</a>
                  </div>
                </div>
                <div className="cu-info-card">
                  <div className="cu-info-icon"><IconClock /></div>
                  <div>
                    <h3>Response time</h3>
                    <p>We typically reply within 1–2 business days.</p>
                  </div>
                </div>
                <div className="cu-info-card">
                  <div className="cu-info-icon"><IconMessageCircle /></div>
                  <div>
                    <h3>Before you write in</h3>
                    <p>Check our <Link to="/site-maps">Site Map</Link> — your answer might already be a click away.</p>
                  </div>
                </div>

                <div className="cu-socials">
                  <a href="https://www.facebook.com/profile.php?id=61591769297796" target="_blank" rel="noreferrer" aria-label="Facebook" className="cu-social-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                  </a>
                  <a href="https://www.instagram.com/acadebility/" target="_blank" rel="noreferrer" aria-label="Instagram" className="cu-social-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>
                  </a>
                  <a href="https://www.linkedin.com/in/acadebility-learning-7573b524a/" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="cu-social-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>
                  </a>
                </div>
              </div>

              {/* ── Form column ── */}
              <div className="cu-form-card">
                {submitted ? (
                  <div className="cu-success">
                    <div className="cu-success-icon"><IconCheckCircle /></div>
                    <h3>Message received</h3>
                    <p>Thanks for reaching out — we'll get back to you soon.</p>
                    <button className="cu-secondary-btn" onClick={() => setSubmitted(false)}>Send another message</button>
                  </div>
                ) : (
                  <form className="cu-form" onSubmit={handleSubmit} noValidate>
                    <h2 className="cu-form-title">Send us a message</h2>

                    <div className="cu-field-row">
                      <div className="cu-field">
                        <label htmlFor="cu-name">Your name</label>
                        <input
                          id="cu-name" type="text" placeholder="e.g. Priya Sharma"
                          value={form.name} onChange={handleChange("name")}
                          className={errors.name ? "cu-input-error" : ""}
                        />
                        {errors.name && <span className="cu-field-error">{errors.name}</span>}
                      </div>
                      <div className="cu-field">
                        <label htmlFor="cu-email">Email address</label>
                        <input
                          id="cu-email" type="email" placeholder="you@example.com"
                          value={form.email} onChange={handleChange("email")}
                          className={errors.email ? "cu-input-error" : ""}
                        />
                        {errors.email && <span className="cu-field-error">{errors.email}</span>}
                      </div>
                    </div>

                    <div className="cu-field">
                      <label htmlFor="cu-subject">Subject</label>
                      <input
                        id="cu-subject" type="text" placeholder="What's this about?"
                        value={form.subject} onChange={handleChange("subject")}
                        className={errors.subject ? "cu-input-error" : ""}
                      />
                      {errors.subject && <span className="cu-field-error">{errors.subject}</span>}
                    </div>

                    <div className="cu-field">
                      <label htmlFor="cu-message">Message</label>
                      <textarea
                        id="cu-message" rows={6} placeholder="Tell us a bit more…"
                        value={form.message} onChange={handleChange("message")}
                        className={errors.message ? "cu-input-error" : ""}
                      />
                      {errors.message && <span className="cu-field-error">{errors.message}</span>}
                    </div>

                    <button type="submit" className="cu-submit-btn" disabled={submitting}>
                      {submitting ? (
                        <><span className="cu-spinner" /> Sending…</>
                      ) : (
                        <>Send message <IconSend /></>
                      )}
                    </button>
                  </form>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactUs;